<script lang="ts" setup>
    import { reactive, computed } from 'vue';
    import { SingleArrowLeftSVG, SingleArrowRightSVG } from '@cwd/shared/assets/vector/ui/IconsUI';
    import { uid } from '@utils';
    import { propogateForDataset } from '@shared/utils/DOM';

    const props = defineProps<{
        modelValue: string | null;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: string | null];
    }>();

    const instanceId = uid();
    const instanceIdRegexp = new RegExp(instanceId, 'g');

    const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const parsed = computed(() => {
        if (!props.modelValue) return null;
        const [y, m] = props.modelValue.split('-').map(Number);
        return isNaN(y) || isNaN(m) ? null : { year: y, month: m };
    });

    const displayValue = computed(() => {
        if (!parsed.value) return '';
        return `${MONTH_LABELS[parsed.value.month - 1]} ${parsed.value.year}`;
    });

    const nav = reactive({
        year: parsed.value?.year ?? new Date().getFullYear(),
    });

    const pickerOpen = reactive({ open: false, position: 'btm' as 'btm' | 'top' });

    function openPicker(ev: MouseEvent): void {
        if (pickerOpen.open) return;
        pickerOpen.open = true;

        const target = ev.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        pickerOpen.position = window.innerHeight - rect.bottom < 260 ? 'top' : 'btm';

        let listener: (e: MouseEvent) => void;
        document.addEventListener(
            'mouseup',
            (listener = (e: MouseEvent) => {
                if (propogateForDataset(e.target as HTMLElement, instanceIdRegexp)) return;
                pickerOpen.open = false;
                document.removeEventListener('mouseup', listener);
            }),
        );
    }

    function selectMonth(month: number): void {
        emit('update:modelValue', `${nav.year}-${String(month).padStart(2, '0')}`);
        pickerOpen.open = false;
    }

    function prevYear(): void {
        nav.year--;
    }
    function nextYear(): void {
        nav.year++;
    }
</script>

<script lang="ts">
    export default { name: 'MonthPicker' };
</script>

<template>
    <div class="picker-container" :class="pickerOpen.open && 'active'">
        <div class="input-group" style="cursor: pointer" :data-type="`pickerinput_${instanceId}`" @mousedown.prevent="openPicker($event)">
            <input
                readonly
                class="form-control"
                type="text"
                :value="displayValue"
                placeholder="Select month"
                style="cursor: pointer; background: #fff" />
            <span class="input-group-addon">
                <span class="glyphicon glyphicon-calendar"></span>
            </span>
        </div>

        <div
            v-if="pickerOpen.open"
            class="picker-dd month-picker-dd"
            :class="pickerOpen.position"
            :data-type="`picker_${instanceId}`">
            <div class="picker-body" style="flex-direction: column; padding: 10px">
                <!-- Year nav -->
                <div class="d-flex align-items-center" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px">
                    <button type="button" class="btn btn-subtle btn-sm" @mousedown.prevent="prevYear">
                        <component :is="SingleArrowLeftSVG" />
                    </button>
                    <strong>{{ nav.year }}</strong>
                    <button type="button" class="btn btn-subtle btn-sm" @mousedown.prevent="nextYear">
                        <component :is="SingleArrowRightSVG" />
                    </button>
                </div>

                <!-- Month grid -->
                <div class="month-picker-grid">
                    <button
                        v-for="(label, idx) in MONTH_LABELS"
                        :key="label"
                        type="button"
                        class="month-picker-cell"
                        :class="{
                            selected: parsed?.year === nav.year && parsed?.month === idx + 1,
                            today: new Date().getFullYear() === nav.year && new Date().getMonth() === idx,
                        }"
                        @mousedown.prevent="selectMonth(idx + 1)">
                        {{ label }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
    .month-picker-dd {
        width: 200px;
    }

    .month-picker-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 4px;
    }

    .month-picker-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 6px 4px;
        border: none;
        background-color: #f5f5f5;
        border-radius: var(--border-radius, 4px);
        cursor: pointer;
        font-size: 13px;
        user-select: none;
    }

    .month-picker-cell:hover {
        background-color: var(--brand-primary-500, #337ab7);
        color: #fff;
    }

    .month-picker-cell.today {
        background-color: var(--brand-primary-500, #337ab7);
        color: #fff;
        font-weight: 600;
    }

    .month-picker-cell.selected {
        background-color: var(--brand-cta, #286090);
        color: #fff;
        font-weight: 600;
    }
</style>
